import { atom, useAtomValue } from 'jotai'
import type { Atom } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { useCallback } from 'react'
import { z } from 'zod'
import { validateShiftJisResult } from '~/features/reservationInput/store/userInput'
import type { ValidationStatus } from '~/lib/encoding/shiftjis'
import { ValidateShiftJis, errorMessage as shiftJisErrorMessage } from '~/lib/encoding/shiftjis'
import type { ValidateShiftJisQueryVariables } from '~/lib/graphql/graphql'
import { createQueryAtom, createQueryOptions } from '~/stores/jotaiQueryHelper'
import { requestsAndComments$ } from './stateMachine/context'
import { useTransition } from './stateMachine/state'

export const requestsAndCommentsError$ = atom((get) => {
  return validateRequestsAndComments(
    get(requestsCommentsShiftJisQueryResult$),
    get(requestsAndComments$)
  )
})
requestsAndComments$.debugLabel = 'requestsAndComments$'

export function useRequestsAndCommentsError() {
  return useAtomValue(requestsAndCommentsError$)
}

const requestsAndCommentsSchema = z
  .string()
  .max(200, { message: 'ご要望・コメントは200文字以内で入力してください' })
  .refine(
    (v) => {
      return validateChars(v)
    },
    {
      message:
        'ご要望・コメントには絵文字、または次の文字は使用できません <>#%{}|^\\`"\'〔〕［］｛｝〈〉《》『』≦≧≪≫⇒⇔℃＊☆★◎◇◆△▲▽▼♯♭♪♬♫♥♡❤',
    }
  )

function validateChars(v: string) {
  return !(
    /[<>#%{}|^`"'〔〕［］｛｝〈〉《》『』≦≧≪≫⇒⇔℃＊☆★◎◇◆△▲▽▼♯♭♪]/.test(v) ||
    // ハートはエラーにする
    /[\u2764|\u2661|\u2665]/g.test(v) ||
    // ♪♬♫はエラーにする
    /[\u266a-\u266c]/g.test(v) ||
    // 絵文字チェック
    /[\uD800-\uDBFF][\uDC00-\uDFFF]/g.test(v)
  )
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('validateChars', () => {
    expect(validateChars('happy birthday')).toBe(true)
    expect(validateChars('happy birthday♥♡❤')).toBe(false)
    expect(validateChars('happy birthday👍')).toBe(false)
    expect(validateChars('happy birthday♪♬♫')).toBe(false)
    expect(
      validateChars('happy birthday <>#%{}|^`"\'〔〕［］｛｝〈〉《》『』≦≧≪≫⇒⇔℃＊☆★◎◇◆△▲▽▼♯♭♪')
    ).toBe(false)
  })
}

const requestsCommentsShiftJisQueryVariables$: Atom<ValidateShiftJisQueryVariables> = atom(
  (get) => {
    return {
      input: get(requestsAndComments$),
    }
  }
)
requestsCommentsShiftJisQueryVariables$.debugLabel = 'requestsCommentsShiftJisQueryVariables$'

const requestCommentsShiftJisQueryFn$ = createQueryAtom<typeof ValidateShiftJis>()
requestCommentsShiftJisQueryFn$.debugLabel = 'requestCommentsShiftJisQueryFn$'

const requestsCommentsShiftJisQuery$ = atomWithQuery((get) =>
  createQueryOptions(
    ValidateShiftJis,
    get(requestsCommentsShiftJisQueryVariables$),
    get(requestCommentsShiftJisQueryFn$).fn,
    { staleTime: 60 * 1000 }
  )
)
requestsCommentsShiftJisQuery$.debugLabel = 'requestsCommentsShiftJisQuery$'

const requestsCommentsShiftJisQueryResult$: Atom<ValidationStatus> = atom((get) => {
  return validateShiftJisResult(get(requestsCommentsShiftJisQuery$))
})
requestsCommentsShiftJisQueryResult$.debugLabel = 'requestsCommentsShiftJisQueryResult$'

function validateRequestsAndComments(status: ValidationStatus, v: string) {
  if (v === '') {
    return ''
  }
  const result = requestsAndCommentsSchema.safeParse(v)
  if (!result.success) {
    return result.error.errors[0].message
  }
  return shiftJisErrorMessage('ご要望・コメント', status, { max: 400 })
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('validateRequestsAndComments', () => {
    const status: ValidationStatus = { status: 'valid', bytesLen: 3 }
    expect(validateRequestsAndComments(status, '')).toBe('')
    expect(validateRequestsAndComments(status, 'a'.repeat(200))).toBe('')
    expect(validateRequestsAndComments(status, 'a'.repeat(201))).toBe(
      'ご要望・コメントは200文字以内で入力してください'
    )
    expect(validateRequestsAndComments(status, 'あ'.repeat(200))).toBe('')
    expect(validateRequestsAndComments(status, 'あ'.repeat(201))).toBe(
      'ご要望・コメントは200文字以内で入力してください'
    )
    expect(validateRequestsAndComments(status, '♪')).toMatch(
      /^ご要望・コメントには絵文字、または次の文字は使用できません/
    )
  })
}

export function useSetRequestsAndComments() {
  const send = useTransition()
  return useCallback(
    (value: string) => {
      send({
        type: 'updated',
        requestsAndComments: value,
      })
    },
    [send]
  )
}
